/**
 * useThrottledTicks — returns a snapshot of symbolTicks that updates at most
 * once every `intervalMs` milliseconds (default 5 s).
 *
 * Why this exists:
 *   WebSocket ticks can arrive multiple times per second. Using the raw
 *   symbolTicks from useWebSocket() as a dep for table/sort computations
 *   causes every table on the page to re-sort and re-render on each tick,
 *   producing visible row-shuffling and header flicker.
 *
 *   This hook keeps a ref to the latest ticks (always fresh, no stale closures)
 *   but only pushes a new state value every 5 s so React only re-renders
 *   display components at that cadence.
 *
 * Usage:
 *   // Replace symbolTicks from useWebSocket() with this for display/table work:
 *   const symbolTicks = useThrottledTicks()          // 5 s default
 *   const symbolTicks = useThrottledTicks(3000)      // custom interval
 *
 *   // Keep the raw useWebSocket() symbolTicks only for real-time writes (e.g.
 *   // flushLTPsToDb) where latency matters.
 */

import { useEffect, useRef, useState } from 'react'
import { useWebSocket } from '../contexts/WebSocketContext'

export function useThrottledTicks(intervalMs = 5000) {
  const { symbolTicks } = useWebSocket()

  // Always reflects the very latest ticks without causing re-renders
  const latestRef = useRef(symbolTicks)
  latestRef.current = symbolTicks

  // State updates at most once per intervalMs
  const [throttled, setThrottled] = useState(symbolTicks)

  useEffect(() => {
    const id = setInterval(() => {
      setThrottled(prev => {
        const latest = latestRef.current
        // Never downgrade: if we have cached ticks and the latest snapshot is
        // momentarily empty (e.g. WS reconnect gap), keep the previous ticks so
        // price-based sorts don't suddenly show all-zero values.
        if (!latest || Object.keys(latest).length === 0) return prev
        return { ...latest }
      })
    }, intervalMs)
    return () => clearInterval(id)
  }, [intervalMs])

  return throttled
}
